import { Button, cn } from "@win-reality/react-ui";
import { useState } from "react";

import { useNow } from "../../hooks/useNow";
import { formatRemaining, getRemainingMs } from "../../utils/gameEndTime";
import { SIDEBAR_BUTTON_CLASS } from "../sidebarButton";
import { GameEndTimeDialog } from "./GameEndTimeDialog";

type GameEndTimeProps = {
  /** When the game is scheduled to end (ISO); nothing shows without one */
  endTime: string | undefined;
  gameId: string;
  machineUuid: string;
  /** Saves a new end (epoch seconds); resolves to false if it failed */
  onChangeEndTime: (endTimeS: number) => Promise<boolean>;
  /** Opens End Game */
  onEndGame: () => void;
};

/** Time Remaining: counts down to the game's scheduled end, and moves it. */
export const GameEndTime = ({
  endTime,
  gameId,
  machineUuid,
  onChangeEndTime,
  onEndGame,
}: GameEndTimeProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const now = useNow(Boolean(endTime));
  const remainingMs = getRemainingMs(endTime, now);
  if (remainingMs === null) return null;

  const remaining = formatRemaining(remainingMs);

  return (
    <>
      <Button
        variant="secondary-flat-dark-outline"
        size="sm"
        onClick={() => setIsOpen(true)}
        data-testid="game-end-time-button"
        data-value={endTime}
        className={cn(SIDEBAR_BUTTON_CLASS, "font-regular")}
      >
        Time Remaining:&nbsp;<span className="font-bold">{remaining}</span>
      </Button>

      {isOpen && (
        <GameEndTimeDialog
          remaining={remaining}
          endTimeMs={Date.parse(endTime!)}
          now={now}
          gameId={gameId}
          machineUuid={machineUuid}
          onClose={() => setIsOpen(false)}
          onSave={async (endTimeS) => {
            if (await onChangeEndTime(endTimeS)) setIsOpen(false);
          }}
          onEndGame={() => {
            setIsOpen(false);
            onEndGame();
          }}
        />
      )}
    </>
  );
};
